export type InAppDialogTone = 'primary' | 'danger';

export type InAppDialogRequest = {
  id: string;
  mode: 'alert' | 'confirm';
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone: InAppDialogTone;
  resolve?: (confirmed: boolean) => void;
};

type InAppDialogListener = (request: InAppDialogRequest) => void;

const listeners = new Set<InAppDialogListener>();
const backlog: InAppDialogRequest[] = [];

let sequence = 0;
let alertOverrideInstalled = false;
let nativeAlert: typeof window.alert | null = null;

const nextDialogId = () => {
  sequence += 1;
  return `in-app-dialog-${Date.now()}-${sequence}`;
};

const publish = (request: InAppDialogRequest) => {
  if (listeners.size === 0) {
    backlog.push(request);
    return;
  }
  listeners.forEach(listener => listener(request));
};

export const subscribeInAppDialogs = (listener: InAppDialogListener) => {
  listeners.add(listener);
  if (backlog.length > 0) {
    const pending = backlog.splice(0, backlog.length);
    pending.forEach(request => listener(request));
  }
  return () => {
    listeners.delete(listener);
  };
};

export const showInAppAlert = (
  message: string,
  options?: { title?: string; confirmLabel?: string; tone?: InAppDialogTone }
) => {
  publish({
    id: nextDialogId(),
    mode: 'alert',
    title: options?.title || 'Notice',
    message: String(message || ''),
    confirmLabel: options?.confirmLabel || 'OK',
    tone: options?.tone || 'primary'
  });
};

export const showInAppConfirm = (
  message: string,
  options?: {
    title?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    tone?: InAppDialogTone;
  }
) =>
  new Promise<boolean>(resolve => {
    publish({
      id: nextDialogId(),
      mode: 'confirm',
      title: options?.title || 'Please confirm',
      message: String(message || ''),
      confirmLabel: options?.confirmLabel || 'Confirm',
      cancelLabel: options?.cancelLabel || 'Cancel',
      tone: options?.tone || 'primary',
      resolve
    });
  });

export const installInAppAlertOverride = () => {
  if (typeof window === 'undefined' || alertOverrideInstalled) return;
  nativeAlert = window.alert.bind(window);
  window.alert = (message?: unknown) => {
    showInAppAlert(String(message ?? ''), { title: 'Notice' });
  };
  alertOverrideInstalled = true;
};

export const restoreNativeAlert = () => {
  if (!alertOverrideInstalled || typeof window === 'undefined' || !nativeAlert) return;
  window.alert = nativeAlert;
  nativeAlert = null;
  alertOverrideInstalled = false;
};
